import { createUserStore } from '../../stores/userStore';
import { AUTH_STORAGE_KEYS } from '../../config/constants';
import type { ApiService } from '../api';

/**
 * Recovers a practitioner's (SRA) own login session out of the raw, unnamespaced
 * storage keys `userStore.ts` writes — used by AdminView's debug Page/Calc Logic save
 * when it's actually mounted under a respondent/secure-token session (see
 * `respondentSessionStore.ts`, which namespaces its own keys under `secure_session:`
 * specifically so it never collides with these). A practitioner who opens a `/secure`
 * link from within a debug session leaves their own session sitting untouched in the
 * same origin storage the new tab can also read — this lets the debug-save action
 * borrow it for just that one privileged call, instead of authorizing with the
 * respondent session's token (which the backend correctly rejects for authoring).
 */

/** Pure presence check, no side effects — used to decide whether to show the debug
 * editor at all in a respondent session, mirroring userStore's own checkStoredAuth bar. */
export async function hasStoredPractitionerSession(storage: any): Promise<boolean> {
  if (!storage?.getItem) {
    return false;
  }
  const [token, session] = await Promise.all([
    storage.getItem(AUTH_STORAGE_KEYS.TOKEN),
    storage.getItem(AUTH_STORAGE_KEYS.USER_SESSION),
  ]);
  return !!(token && session);
}

/**
 * Runs `fn` authorized as the practitioner whose session is sitting in the raw storage,
 * temporarily swapping the given ApiService's active token to theirs and handing `fn` a
 * throwaway userStore pointed at that same practitioner session (so a mid-call 401 refresh
 * uses the practitioner's own refresh token, not the respondent session's) — then restores
 * the respondent's token afterward, regardless of success or failure.
 */
export async function withPractitionerAuth<T>(
  apiService: ApiService,
  storage: any,
  fn: (practitionerUserStore: ReturnType<typeof createUserStore>) => Promise<T>
): Promise<T> {
  const respondentToken = apiService.getAuthToken();
  const practitionerStore = createUserStore({ storage, apiService });

  const recovered = await practitionerStore.getState().checkStoredAuth();
  if (!recovered) {
    throw new Error('No practitioner session found — reconnect in the original tab and try again.');
  }

  try {
    return await fn(practitionerStore);
  } finally {
    apiService.setAuthToken(respondentToken);
  }
}
